import { Mutator } from 'final-form';
import {
  Registration,
  Brand,
  RegistrationFieldPermissionsField,
} from '@stax-core/types-omni';

import {
  PaymentsFieldsValues,
  PaymentsFieldsErrors,
} from './PaymentsFields.types';
import { isEmpty } from '../../util/object.util';
import { isWithinRange } from '../../util/validator.util';
import { formatToPlainNumber } from '../../util/format.util';
import { getCombinedFieldsVisibilityLevels } from '../../hooks';
import { reasonsForApplyingToProcChange } from '../../util/registration.util';

type OptionalBool = '0' | '1' | '';
const accountDataCompromisedToRegistration = (
  isCompromised: OptionalBool,
  isRemediated: OptionalBool,
): Registration['pci_compromise_type'] =>
  isCompromised === '0'
    ? 'NOT_NOTIFY'
    : isRemediated === '1'
    ? 'NOTIFY_ACTION_DONE'
    : isRemediated === '0'
    ? 'NOTIFY_ACTION_NOT_STARTED'
    : null;

export function mapFormValuesToPayload(
  formValues: PaymentsFieldsValues,
): Partial<Registration> {
  // if this merchant already has a pricing_plan set, use it
  // otherwise, find an appropriate pricing_plan based off of the chosen_processing_method
  const pricingPlan = formValues.pricing_plan
    ? formValues.pricing_plan
    : formValues.chosen_processing_method === 'in-person'
    ? 'interchange0-15'
    : 'interchange0-18';

  // prettier-ignore
  const payload: Partial<Registration> = {
    reason_for_applying: formValues.reason_for_applying,
    refund_policy: formValues.refund_policy,
    service_you_provide: formValues.service_you_provide || '',
    chosen_processing_method: formValues.chosen_processing_method,
    pricing_plan: pricingPlan,

    amex_direct_number: formatToPlainNumber(formValues.amex_direct_number),
    amex_annual_volume: formatToPlainNumber(formValues.amex_annual_volume),
    annual_volume: formatToPlainNumber(formValues.annual_volume),
    avg_trans_size: formatToPlainNumber(formValues.avg_trans_size),
    highest_trans_amount: formatToPlainNumber(formValues.highest_trans_amount),
    card_present_percent: formatToPlainNumber(formValues.card_present_percent),
    b2b_percent: formatToPlainNumber(formValues.b2b_percent),
    international: formatToPlainNumber(formValues.international),
    card_not_present_percent: formatToPlainNumber(
      100 - +formValues.card_present_percent!,
    ),
    annual_gross_ach_revenue: formatToPlainNumber(
      formValues.annual_gross_ach_revenue,
    ),
    avg_ach_transaction: formatToPlainNumber(formValues.avg_ach_transaction),
    largest_ach_transaction: formatToPlainNumber(
      formValues.largest_ach_transaction,
    ),
    annual_gross_shopping_cart_revenue: formatToPlainNumber(
      formValues.annual_gross_shopping_cart_revenue,
    ),
    avg_shopping_cart_transaction: formatToPlainNumber(
      formValues.avg_shopping_cart_transaction,
    ),
    largest_shopping_cart_transaction: formatToPlainNumber(
      formValues.largest_shopping_cart_transaction,
    ),
    shopping_cart_card_present_percent: formatToPlainNumber(
      formValues.shopping_cart_card_present_percent,
    ),
    shopping_cart_b2b_percent: formatToPlainNumber(
      formValues.shopping_cart_b2b_percent,
    ),
    shopping_cart_international: formatToPlainNumber(
      formValues.shopping_cart_international,
    ),
    mcc: formValues.mcc,
    pci_compromise_type: accountDataCompromisedToRegistration(
      formValues.account_data_compromised,
      formValues.account_data_compromised_remediated,
    ),
  };
  if (payload.reason_for_applying) {
    const reasonToProcChange = reasonsForApplyingToProcChange.find(
      reason => reason.reasonForApplyingValue === payload.reason_for_applying,
    );
    if (reasonToProcChange) {
      payload.proc_change = reasonToProcChange.procChangeValue;
    }
  }
  return payload;
}

const dataCompromisedFields: Partial<Registration['pci_compromise_type']>[] = [
  'NOTIFY_ACTION_DONE',
  'NOTIFY_ACTION_NOT_STARTED',
];

export function mapRegistrationToInitialFormValues(
  registration: Registration | null | undefined,
): PaymentsFieldsValues {
  if (!registration) return {} as PaymentsFieldsValues;

  // prettier-ignore
  return {
    reason_for_applying: registration.reason_for_applying,
    refund_policy: registration.refund_policy,
    chosen_processing_method: registration.chosen_processing_method,
    service_you_provide: registration.service_you_provide,
    pricing_plan: registration.pricing_plan,

    amex_direct_number: formatToPlainNumber(registration.amex_direct_number),
    amex_annual_volume: formatToPlainNumber(registration.amex_annual_volume),
    annual_volume: formatToPlainNumber(registration.annual_volume),
    avg_trans_size: formatToPlainNumber(registration.avg_trans_size),
    highest_trans_amount: formatToPlainNumber(
      registration.highest_trans_amount,
    ),
    card_present_percent: formatToPlainNumber(
      registration.card_present_percent,
    ),
    b2b_percent: formatToPlainNumber(registration.b2b_percent),
    international: formatToPlainNumber(registration.international),
    annual_gross_ach_revenue: formatToPlainNumber(
      registration.annual_gross_ach_revenue,
    ),
    avg_ach_transaction: formatToPlainNumber(registration.avg_ach_transaction),
    largest_ach_transaction: formatToPlainNumber(
      registration.largest_ach_transaction,
    ),
    annual_gross_shopping_cart_revenue: formatToPlainNumber(
      registration.annual_gross_shopping_cart_revenue,
    ),
    avg_shopping_cart_transaction: formatToPlainNumber(
      registration.avg_shopping_cart_transaction,
    ),
    largest_shopping_cart_transaction: formatToPlainNumber(
      registration.largest_shopping_cart_transaction,
    ),
    shopping_cart_card_present_percent: formatToPlainNumber(
      registration.shopping_cart_card_present_percent,
    ),
    shopping_cart_b2b_percent: formatToPlainNumber(
      registration.shopping_cart_b2b_percent,
    ),
    shopping_cart_international: formatToPlainNumber(
      registration.shopping_cart_international,
    ),

    shouldAutoCalculateACHValues: shouldAutoCalculateACHValues(registration),

    mcc: registration.mcc,
    account_data_compromised: registration.pci_compromise_type
      ? (Number(
          dataCompromisedFields.includes(registration.pci_compromise_type),
        ).toString() as '0' | '1')
      : '',
    account_data_compromised_remediated: registration.pci_compromise_type
      ? (Number(
          registration.pci_compromise_type === 'NOTIFY_ACTION_DONE',
        ).toString() as '0' | '1')
      : '',
  };
}

export function validateRequiredFormValues(
  values: PaymentsFieldsValues,
  isPayfac: boolean,
): PaymentsFieldsErrors {
  const errors = {} as PaymentsFieldsErrors;

  const sharedRequiredFields = [
    'reason_for_applying',
    'refund_policy',
    'service_you_provide',
    'annual_volume',
    'avg_trans_size',
    'highest_trans_amount',
    'card_present_percent',
    'b2b_percent',
    'international',
    'chosen_processing_method',
  ];

  const requiredFields = isPayfac
    ? sharedRequiredFields
    : sharedRequiredFields.concat(['account_data_compromised']);

  requiredFields.forEach(field => {
    if (!values[field]) errors[field] = 'Required';
  });

  if (
    values.account_data_compromised === '1' &&
    !values.account_data_compromised_remediated
  ) {
    errors.account_data_compromised_remediated = 'Required';
  }

  if (
    isPayfac &&
    Number.parseInt(values.annual_volume ?? '0') >= 1000000 &&
    !values.amex_annual_volume
  ) {
    errors.amex_annual_volume = 'Required';
  }

  return errors;
}

export function validateFormValues(
  values: PaymentsFieldsValues,
): PaymentsFieldsErrors {
  const errors = {} as PaymentsFieldsErrors;

  const percentageFields = [
    'card_present_percent',
    'b2b_percent',
    'international',
  ];

  percentageFields.forEach(field => {
    const value = +formatToPlainNumber(values[field]);
    if (!isWithinRange(value, 0, 100)) {
      errors[field] = 'Must be between 0 and 100';
    }
  });

  const numericFields = [
    'amex_direct_number',
    'amex_annual_volume',
    'annual_volume',
    'avg_trans_size',
    'highest_trans_amount',
    'card_present_percent',
    'b2b_percent',
    'international',
  ];

  numericFields.forEach(field => {
    const value = formatToPlainNumber(values[field]);
    if (isNaN(+value)) errors[field] = 'Must be a number';
  });

  if (values.amex_direct_number && values.amex_direct_number.length !== 10) {
    errors.amex_direct_number = 'Number must be 10 digits';
  }

  return errors;
}

export function calculateAchValuesFromCreditCardValues(
  formValues: Pick<
    Partial<Registration>,
    'annual_volume' | 'avg_trans_size' | 'highest_trans_amount'
  >,
): Pick<
  Partial<Registration>,
  'annual_gross_ach_revenue' | 'avg_ach_transaction' | 'largest_ach_transaction'
> {
  return {
    // if formValues.annual_volume is null or undefined, just make annual_gross_ach_revenue the same
    // else if formValues.annual_volume is a number, then set it to be equal to 40% of formValues.annual_volume
    annual_gross_ach_revenue: Number.isNaN(
      parseFloat(formValues.annual_volume!),
    )
      ? formValues.annual_volume
      : (+formValues.annual_volume! * 0.4).toFixed(0),
    avg_ach_transaction: formValues.avg_trans_size,
    largest_ach_transaction: formValues.highest_trans_amount,
  };
}

export function shouldAutoCalculateACHValues(
  registration: Pick<
    Partial<Registration>,
    | 'annual_volume'
    | 'avg_trans_size'
    | 'highest_trans_amount'
    | 'annual_gross_ach_revenue'
    | 'avg_ach_transaction'
    | 'largest_ach_transaction'
  >,
) {
  const r = registration;

  if (isEmpty(registration)) {
    // if values are empty (form is loading), then return true
    return true;
  } else if (
    r.annual_gross_ach_revenue === null &&
    r.largest_ach_transaction === null &&
    r.avg_ach_transaction === null
  ) {
    // else if all of the values are null (have not been set yet), then return true
    // NOTE: if you manually clear out these fields using self reg, they'll be set to empty string
    // so we can tell when they've manually been cleared out or not
    return true;
  } else {
    // else if all of the values match the expected calculated values, then return true
    const expectedValues = calculateAchValuesFromCreditCardValues(r);

    return (
      expectedValues.annual_gross_ach_revenue === r.annual_gross_ach_revenue &&
      expectedValues.largest_ach_transaction === r.largest_ach_transaction &&
      expectedValues.avg_ach_transaction === r.avg_ach_transaction
    );
  }
}

export const formMutators: { [key: string]: Mutator<PaymentsFieldsValues> } = {
  setShouldAutoCalculateACHValues: (_, state, utils) => {
    const formValues = state.formState.values as PaymentsFieldsValues;
    const oldValue = formValues.shouldAutoCalculateACHValues;
    if (!oldValue) return;

    const newValue = shouldAutoCalculateACHValues(formValues);
    if (oldValue !== newValue)
      utils.changeValue(state, 'shouldAutoCalculateACHValues', _ => newValue);
  },
  clearCalculatedAchFields: (_, state, utils) => {
    utils.changeValue(state, 'annual_gross_ach_revenue', _ => '');
    utils.changeValue(state, 'avg_ach_transaction', _ => '');
    utils.changeValue(state, 'largest_ach_transaction', _ => '');
  },
  setCalculatedAchFields: (_, state, utils) => {
    const formValues = state.formState.values as PaymentsFieldsValues;
    if (!formValues.shouldAutoCalculateACHValues) return;

    const calculatedValues = calculateAchValuesFromCreditCardValues(formValues);
    Object.keys(calculatedValues).forEach(field => {
      utils.changeValue(state, field, _ => calculatedValues[field]);
    });
  },
};
export const necessaryPaymentsFields: RegistrationFieldPermissionsField[] = [
  'reason_for_applying',
  'refund_policy',
  'chosen_processing_method',
  'service_you_provide',
  'annual_gross_ach_revenue',
  'avg_ach_transaction',
  'largest_ach_transaction',
];

export function arePaymentsFieldsHidden(
  brand: Brand | null,
  registration: Registration | null,
) {
  const { none } = getCombinedFieldsVisibilityLevels(
    registration,
    brand,
    ...necessaryPaymentsFields,
  );

  /**
   * IF none of the necessary fields are visible (i.e. they have been prefilled or otherwise turned off)
   * THEN return true (do not show this page)
   */
  return none;
}

export function isPaymentsPageHidden(brand: Brand) {
  return !brand.regSidebarItems?.includes('Payments');
}
